import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Col, Row } from '../../lib/utils';
import { Head4, Body3Bold, Body3Regular, CaptionRegular } from '../../lib/font';
import { colors } from '../../lib/colors';
import { formatAmount, recipientSummary } from './lib/format';
import { getCsrfToken } from './lib/session';
import { PaymentOrderView } from './types';
import PaymentStateScreen from './components/PaymentStateScreen';
import { getOrCreatePaymentCompletion } from './lib/completion';

const SummaryRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <Row style={{ alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, padding: '8px 0' }}>
        <Body3Regular style={{ color: colors.LAVEL_1, flexShrink: 0 }}>{label}</Body3Regular>
        <Body3Regular style={{ color: colors.LAVEL_3, fontWeight: 600, textAlign: 'right' }}>
            {value}
        </Body3Regular>
    </Row>
);

type CompleteState =
    | { kind: 'loading' }
    | { kind: 'success'; order: PaymentOrderView }
    | { kind: 'processing'; status: string }
    | { kind: 'error'; message: string };

const GiftPayCompletePage: React.FC = () => {
    const { orderId } = useParams<{ orderId: string }>();
    const [searchParams] = useSearchParams();
    const [state, setState] = useState<CompleteState>({ kind: 'loading' });

    const paymentKey = searchParams.get('paymentKey');
    const redirectOrderId = searchParams.get('orderId');
    const amountParam = searchParams.get('amount');

    useEffect(() => {
        let active = true;

        const completePayment = async () => {
            if (!orderId || !paymentKey || redirectOrderId !== orderId || !amountParam) {
                throw new Error('결제 승인 정보가 올바르지 않아요.');
            }

            const amount = Number(amountParam);
            if (!Number.isFinite(amount) || amount <= 0) {
                throw new Error('결제 금액이 올바르지 않아요.');
            }

            const csrfToken = getCsrfToken(orderId);
            const result = await getOrCreatePaymentCompletion(
                orderId,
                paymentKey,
                amount,
                csrfToken,
            );
            if (!active) return;
            if (result.kind === 'success') {
                setState(result);
            } else {
                setState(result);
            }
        };

        completePayment().catch((error: unknown) => {
            if (active) {
                setState({
                    kind: 'error',
                    message: error instanceof Error ? error.message : '결제 승인 중 오류가 발생했어요.',
                });
            }
        });

        return () => {
            active = false;
        };
    }, [amountParam, orderId, paymentKey, redirectOrderId]);

    if (state.kind === 'loading') {
        return <PaymentStateScreen title="결제 승인과 선물 발송을 처리하고 있어요" description="잠시만 기다려 주세요." />;
    }

    if (state.kind === 'processing') {
        return (
            <PaymentStateScreen
                title="선물을 처리하고 있어요"
                description={`현재 상태: ${state.status}. 잠시 후 다시 확인해 주세요.`}
            />
        );
    }

    if (state.kind === 'error') {
        return <PaymentStateScreen title="결제를 완료하지 못했어요" description={state.message} isError />;
    }

    const { order } = state;

    return (
        <Col style={{ width: '100%', minHeight: '100vh', alignItems: 'center', backgroundColor: '#F5F7FA' }}>
            <Col
                style={{
                    width: '100%',
                    maxWidth: 480,
                    minHeight: '100vh',
                    padding: '0 16px',
                    paddingBottom: 'max(24px, env(safe-area-inset-bottom))',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <div
                    style={{
                        width: 64,
                        height: 64,
                        borderRadius: 32,
                        backgroundColor: '#E8F5E9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Check size={32} color="#2E7D32" strokeWidth={3} />
                </div>
                <Head4 style={{ color: colors.LAVEL_4, marginTop: 20 }}>선물이 발송되었어요</Head4>
                <Body3Regular style={{ color: colors.LAVEL_2, marginTop: 8, textAlign: 'center' }}>
                    선물 발급과 전달이 완료됐어요.
                </Body3Regular>

                <Col
                    style={{
                        width: '100%',
                        backgroundColor: '#FFFFFF',
                        borderRadius: 16,
                        padding: '16px 20px',
                        marginTop: 28,
                        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
                    }}
                >
                    <Body3Bold style={{ color: colors.LAVEL_4, marginBottom: 6 }}>결제 내역</Body3Bold>
                    <SummaryRow label="받는 사람" value={recipientSummary(order.recipient)} />
                    <SummaryRow label="상품" value={`${order.product.brand} ${order.product.name}`} />
                    <SummaryRow label="유효기간" value={`발급일로부터 ${order.product.validDays}일`} />
                    <SummaryRow label="결제 금액" value={`${formatAmount(order.amounts.final)}원`} />
                </Col>

                <button
                    onClick={() => window.close()}
                    style={{
                        width: '100%',
                        height: 52,
                        borderRadius: 12,
                        border: 'none',
                        cursor: 'pointer',
                        backgroundColor: colors.primary,
                        marginTop: 28,
                    }}
                >
                    <Body3Bold style={{ color: '#FFFFFF' }}>앱으로 돌아가기</Body3Bold>
                </button>
                <CaptionRegular style={{ color: colors.LAVEL_1, marginTop: 12 }}>
                    이 창은 닫으셔도 됩니다
                </CaptionRegular>
            </Col>
        </Col>
    );
};

export default GiftPayCompletePage;
